import { useEffect, useState } from "react";
import { useUpdateContext } from "../context/UpdateContext";
import { FcGenericSortingAsc, FcGenericSortingDesc } from "react-icons/fc";
import { Prospect } from "../schemas/Prospect";
import supabase from "../services/supabaseClient";
import { useLanguageContext } from "../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../utils";

const ProspectsTerminalPage = () => {
  const [filters, setFilters] = useState({
    levels: ['Freshman', 'Sophomore', 'Junior', 'Senior', 'International'],
    positions: ['Point Guard', 'Combo Guard', 'Wing', 'Combo Forward', 'Power Forward', 'Center']
  });
  // Function to determine the current draft year
  const getCurrentDraftYear = (): string => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    // If it's November or later, show next year's draft
    if (currentMonth >= 10) {
      return (currentYear + 1).toString();
    }
    return currentYear.toString();
  };

  const currentDraftYear = getCurrentDraftYear();
  const previousDraftYear = (parseInt(currentDraftYear) - 1).toString();

  const [activeYear, setActiveYear] = useState<string>(currentDraftYear);
  const [prospects, setProspects] = useState<Prospect[]>(() => {
    const storedData = localStorage.getItem(`prospects_${currentDraftYear}`);
    return storedData ? JSON.parse(storedData) : [];
  });

  const [error, setError] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<{ field: string; direction: string }>({
    field: "last_update",
    direction: "desc",
  });
  const { isHE } = useLanguageContext();
  const { didUpdate } = useUpdateContext();
  const navigate = useNavigate();

  const fetchProspects = async (year: string) => {
    try {
      const { data, error } = await supabase.rpc('get_prospects_list', { draft_year: parseInt(year) });

      if (error) {
        setError(error.message);
      } else if (data) {
        // SORT HERE before setting state and storage
        const sortedData = [...data].sort((a, b) => {
          const aDate = a.last_update ? new Date(a.last_update).getTime() : 0;
          const bDate = b.last_update ? new Date(b.last_update).getTime() : 0;
          return bDate - aDate; // Descending (Latest first)
        });

        setProspects(sortedData);
        localStorage.setItem(`prospects_${year}`, JSON.stringify(sortedData));
      }
    } catch (err) {
      setError("An unexpected error occurred.");
    }
  };

  const loadProspectsFromStorage = (year: string) => {
    const storedData = localStorage.getItem(`prospects_${year}`);
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      const sortedData = [...parsedData].sort((a, b) => {
        const aDate = a.last_update ? new Date(a.last_update).getTime() : null;
        const bDate = b.last_update ? new Date(b.last_update).getTime() : null;

        if (aDate === null && bDate === null) return 0;
        if (aDate === null) return 1;
        if (bDate === null) return -1;
        return bDate - aDate;
      });
      setProspects(sortedData);
      setError(null);
      return true;
    }
    return false;
  };


  const sortProspects = (field: string, direction: string) => {
    const sorted = [...prospects].sort((a, b) => {
      let aValue, bValue;

      if (field === "name") {
        aValue = isHE ? a.full_name[1] : a.full_name[0];
        bValue = isHE ? b.full_name[1] : b.full_name[0];
      } else {
        // last_update
        aValue = a.last_update ? new Date(a.last_update).getTime() : 0;
        bValue = b.last_update ? new Date(b.last_update).getTime() : 0;
      }

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });
    setProspects(sorted);
    setSortOrder({ field, direction });
  };

  const handleYearChange = (year: string) => {
    setActiveYear(year);
    setSortOrder({ field: "last_update", direction: "desc" });
  };

  useEffect(() => {
    const hadCache = loadProspectsFromStorage(activeYear);

    if (!hadCache) {
    fetchProspects(activeYear);
    return;
    }
    if (didUpdate) {
    fetchProspects(activeYear);
    return;
    }

    // We had cache; refresh only if this is the current draft year and we got an update signal
    const isActiveDraft = activeYear === currentDraftYear;
    if (isActiveDraft && didUpdate) {
      fetchProspects(activeYear);
    }

  }, [activeYear, didUpdate, currentDraftYear]);

  const navigateToProspect = (prospect: Prospect) => {
    if (prospect.last_update === null) {
      return;
    } else {
      navigate(`/prospects/${activeYear}/${prospect.id}/${prospect.full_name[0].replace(/\s+/g, '-')}`);
    }
  };

  const toggleAll = (category: 'levels' | 'positions') => {
    const allOptions = {
      levels: ['Freshman', 'Sophomore', 'Junior', 'Senior', 'International'],
      positions: ['Point Guard', 'Combo Guard', 'Wing', 'Combo Forward', 'Power Forward', 'Center']
    };

    setFilters(prev => {
      // If the category is currently empty or partially filled, fill it.
      // If it's already full, clear it.
      const isFull = prev[category].length === allOptions[category].length;
      const updated = isFull ? [] : allOptions[category];

      console.log(`Bulk Toggle [${category}]: ${isFull ? 'Cleared' : 'Selected All'}`);
      return { ...prev, [category]: updated };
    });
  };

  const handleFilterToggle = (category: 'levels' | 'positions', value: string) => {
    setFilters(prev => {
      const currentCategory = prev[category];
      const isSelected = currentCategory.includes(value);

      const updatedCategory = isSelected
        ? currentCategory.filter(item => item !== value) // Remove if exists
        : [...currentCategory, value]; // Add if missing

      const newFilters = { ...prev, [category]: updatedCategory };

      // Console log for debugging before we hit Supabase
      console.log(`Filter Update [${category}]:`, value);
      console.log('Active Filters:', newFilters);

      return newFilters;
    });
  };

  const filteredProspects = prospects.filter((prospect) => {
  // 1. Check Experience Level
  const matchesLevel = filters.levels.includes(prospect.experience_level);

  // 2. Check Position
  // We check the main position (index 0) from your pos_and_roles array
  const currentPos = prospect.pos_and_roles?.[0]?.position || "";
  const matchesPosition = filters.positions.includes(currentPos);

  return matchesLevel && matchesPosition;
});

  // ... rest of your JSX remains the same
  return (
    <div className="min-h-screen bg-terminal flex flex-col items-center p-2">
      {error ? (
        <p className="text-red-500 text-lg">Error loading prospects: {error}</p>
      ) : (
        <>
          {/* --- DRAFT YEAR TABS --- */}
          <div className="flex gap-2 mb-8">
            <button
              onClick={() => handleYearChange(currentDraftYear)}
              className={`px-6 py-2 rounded-t-xl font-bold transition-colors shadow-elevation ${activeYear === currentDraftYear ? "bg-orange-400 text-white" : "bg-slate-100 text-gray-700 hover:bg-slate-200"
                }`}
            >
              {isHE ? `דראפט ${currentDraftYear}` : `${currentDraftYear} Draft`}
            </button>
            <button
              onClick={() => handleYearChange(previousDraftYear)}
              className={`px-6 py-2 rounded-t-xl font-bold transition-colors shadow-elevation ${activeYear === previousDraftYear ? "bg-orange-400 text-white" : "bg-slate-100 text-gray-700 hover:bg-slate-200"
                }`}
            >
              {isHE ? `דראפט ${previousDraftYear}` : `${previousDraftYear} Draft`}
            </button>
          </div>

          {/* --- FILTERS CONTROL BAR --- */}
          <div
            className="w-full max-w-6xl grid grid-cols-2 md:grid-cols-[auto_1fr_auto] items-end gap-y-6 gap-x-2 md:gap-x-12 mb-8 p-4 md:p-6 rounded-2xl bg-white/[0.03] backdrop-blur-sm border border-white/10 shadow-elevation"
            dir={isHE ? 'rtl' : 'ltr'}
          >

            {/* 1. FILTERS SECTION */}
            <div className="col-span-2 md:col-span-1 flex flex-col gap-6 border-gray-800 md:border-x px-2 md:px-8 order-1 md:order-2">

              {/* Level Group */}
              <div className="flex flex-col gap-2">
    <div className="flex items-center gap-2 border-b border-gray-800 pb-1 justify-center md:justify-start">
  <span className={`text-gray-400 font-black text-[10px] md:text-[11px] uppercase tracking-widest ${isHE ? 'font-hebrew italic' : 'font-sans'}`}>
    {isHE ? 'רמת ניסיון' : 'Experience Level'}
  </span>
  <button 
    onClick={() => toggleAll('levels')}
    className="px-2 py-0.5 rounded-md border border-gray-700 bg-gray-800/40 hover:bg-orange-400 hover:text-black hover:border-orange-400 text-orange-400 text-[8px] font-bold uppercase transition-all shadow-elevation active:scale-95"
  >
    {filters.levels.length === 5 ? (isHE ? 'נקה' : 'Clear') : (isHE ? 'הכל' : 'All')}
  </button>
</div>

                <div className="flex flex-row flex-wrap justify-center md:justify-between gap-x-4 gap-y-2">
                  {[
                    { en: 'Freshman', he: 'פרשמן' },
                    { en: 'Sophomore', he: 'סופומור' },
                    { en: 'Junior', he: 'ג׳וניור' },
                    { en: 'Senior', he: 'סיניור' },
                    { en: 'International', he: 'בינלאומי' }
                  ].map((lvl) => (
                    <label key={lvl.en} className="flex items-center gap-1 text-white text-[10px] md:text-xs cursor-pointer hover:text-orange-400 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={filters.levels.includes(lvl.en)}
                        onChange={() => handleFilterToggle('levels', lvl.en)}
                        className="accent-orange-400 h-3 w-3"
                      />
                      {isHE ? lvl.he : lvl.en}
                    </label>
                  ))}
                </div>
              </div>

              {/* Pos Group */}
             <div className="flex flex-col gap-2">
    <div className="flex items-center gap-3 border-b border-gray-800 pb-1 justify-center md:justify-start">
      <span className={`text-gray-400 font-black text-[10px] md:text-[11px] uppercase tracking-widest ${isHE ? 'font-hebrew italic' : 'font-sans'}`}>
        {isHE ? 'עמדה' : 'Position'}
      </span>
      <button 
        onClick={() => toggleAll('positions')}
        className="px-1.5 py-0.5 rounded-md border border-gray-500 bg-gray-900/50 hover:bg-orange-400 hover:text-black hover:border-orange-400 text-orange-400 text-[8px] font-bold uppercase transition-all shadow-elevation active:scale-95"
      >
        {filters.positions.length === 6 ? (isHE ? 'נקה' : 'Clear') : (isHE ? 'הכל' : 'All')}
      </button>
    </div>
                <div className="flex flex-row flex-wrap justify-center md:justify-between gap-x-4 gap-y-2">
                  {[
                    { en: 'Point Guard', he: 'פוינט גארד' },
                    { en: 'Combo Guard', he: 'קומבו גארד' },
                    { en: 'Wing', he: 'ווינג' },
                    { en: 'Combo Forward', he: 'קומבו פורוורד' },
                    { en: 'Power Forward', he: 'פוור פורוורד' },
                    { en: 'Center', he: 'סנטר' }
                  ].map((pos) => (
                    <label key={pos.en} className="flex items-center gap-1 text-white text-[10px] md:text-xs cursor-pointer hover:text-orange-400 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={filters.positions.includes(pos.en)}
                        onChange={() => handleFilterToggle('positions', pos.en)}
                        className="accent-orange-400 h-3 w-3"
                      />
                      {isHE ? pos.he : pos.en}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* 2. SORT NAME: Bottom on mobile (order-2), Left on desktop (order-1) */}
            <div className="flex flex-col items-start order-2 md:order-1">
              <button
                onClick={() => sortProspects("name", sortOrder.direction === "asc" ? "desc" : "asc")}
                className="whitespace-nowrap px-3 py-2 bg-slate-100 text-gray-700 rounded-xl shadow-elevation hover:bg-slate-200 flex items-center gap-1 text-[10px] md:text-sm font-bold active:scale-95 transition-transform"
              >
                {isHE ? 'מיון לפי שם פרטי' : 'Sort by First Name'}
                {sortOrder.field === "name" && (sortOrder.direction === "asc" ? <FcGenericSortingAsc /> : <FcGenericSortingDesc />)}
              </button>
            </div>

            {/* 3. SORT UPDATE: Bottom on mobile (order-3), Right on desktop (order-3) */}
            <div className="flex flex-col items-end order-3">
              <button
                onClick={() => sortProspects("last_update", sortOrder.direction === "asc" ? "desc" : "asc")}
                className="whitespace-nowrap px-3 py-2 bg-slate-100 text-gray-700 rounded-xl shadow-elevation hover:bg-slate-200 flex items-center gap-1 text-[10px] md:text-sm font-bold active:scale-95 transition-transform"
              >
                {isHE ? 'מיון לפי עדכון אחרון' : 'Sort by Last Update'}
                {sortOrder.field === "last_update" && (sortOrder.direction === "asc" ? <FcGenericSortingAsc /> : <FcGenericSortingDesc />)}
              </button>
            </div>
          </div>

          {/* --- TABLE HEADERS --- */}
          <div
            className="hidden md:grid w-full max-w-6xl grid-cols-3 px-4 mb-2 border-b border-gray-800 pb-2 text-gray-500 font-bold text-xs uppercase tracking-widest"
            dir={isHE ? 'rtl' : 'ltr'}
          >
            <span className={isHE ? "text-right" : "text-left"}>{isHE ? 'שם שחקן' : 'Prospect Name'}</span>
            <div className="grid grid-cols-2 text-center">
              <span>{isHE ? 'ניסיון' : 'Experience Level'}</span>
              <span>{isHE ? 'עמדה' : 'Position'}</span>
            </div>
            <span className={isHE ? "text-left" : "text-right"}>{isHE ? 'עדכון אחרון' : 'Last Update'}</span>
          </div>

         {/* --- DATA LIST --- */}
{filteredProspects.length > 0 ? (
  <ul className="w-full max-w-6xl space-y-1" dir={isHE ? 'rtl' : 'ltr'}>
    {filteredProspects.map((prospect) => (
      <li
        key={prospect.id}
        className="px-4 py-4 font-bold rounded-xl grid grid-cols-2 md:grid-cols-3 items-center text-orange-400 text-xl bg-white/[0.02] hover:bg-orange-500/10 hover:text-white hover:shadow-elevation cursor-pointer transition-all border border-white/5"
        onClick={() => navigateToProspect(prospect)}
      >
        <span className={isHE ? "text-right" : "text-left"}>
          {isHE ? prospect.full_name[1] : prospect.full_name[0]}
        </span>

        <div className="hidden md:grid grid-cols-2 text-center items-center">
          <span className="text-white text-sm font-medium border-r border-gray-800 px-2">
            {prospect.experience_level}
          </span>
          <span className="text-white text-sm font-medium px-2">
            {prospect.pos_and_roles?.[isHE ? 1 : 0]?.position}
          </span>
        </div>

        <span className={`text-base text-white font-mono ${isHE ? "text-left" : "text-right"}`}>
          {formatDate(prospect.last_update, isHE)}
        </span>
      </li>
    ))}
  </ul>
) : (
  /* --- EMPTY STATE --- */
  <div className="mt-20 flex flex-col items-center gap-6 text-center">
    <div className="p-4 rounded-2xl bg-gray-900/50 backdrop-blur-sm border border-white/10 shadow-elevation">
      <p className="text-gray-500 text-xl font-medium">
        {isHE ? 'לא נמצאו שחקנים התואמים את הסינון' : 'No prospects match these filters'}
      </p>
    </div>
    <button 
      onClick={() => {
        setFilters({
          levels: ['Freshman', 'Sophomore', 'Junior', 'Senior', 'International'],
          positions: ['Point Guard', 'Combo Guard', 'Wing', 'Combo Forward', 'Power Forward', 'Center']
        })
      }}
      className="px-6 py-2 rounded-xl border border-orange-400 text-orange-400 font-bold shadow-elevation hover:bg-orange-400 hover:text-black transition-all active:scale-95"
    >
      {isHE ? 'אפס את כל המסננים' : 'Reset All Filters'}
    </button>
  </div>
)}
        </>
      )}
    </div>
  );
};

export default ProspectsTerminalPage;